import React, { useEffect, useMemo, useRef, useState } from "react";
import useEquipmentValidation from "../../../hooks/useEquipmentValidation";
import useUserStore from "../../../stores/user";
import Checkbox from "../../Checkbox/Index";
import Select from "../../form/select";
import MultiSelect from "../../form/select/multi";
import StepperInput from "../../form/stepper-input";
import TextInput from "../../form/text-input";
import Button from "../../new-button";
import SlidingTab from "../../SlidingTab/SlidingTab";
import {
  CABLE_LENGTH_OPTIONS,
  CONNECTIVITY_OPTIONS,
  FILE_TYPE_OPTIONS,
  PACKAGE_OPTIONS,
  PACKAGE_PRICING_MAPPING,
  PURCHASE_TYPE_MAPPINGS,
} from "./constants";
import {
  getButtonText,
  getEnabledPurchaseOptions,
  getPurchaseType,
} from "./helpers";
import styles from "./styles.module.scss";

const EquipmentConfigurator = ({
  equipment,
  allEquipments,
  allSelectedEquipments,
  handleAddConfiguredEquipment,
  onEquipmentChange,
}) => {
  const defaultValues = useRef({});
  const { validateEquipment } = useEquipmentValidation();
  const userOrgIsPaychex = useUserStore((state) => state.userOrgIsPaychex);
  const [errorFields, setErrorFields] = useState(new Set());
  const equipmentType = equipment.equipment_type;
  const isNotAGateway = equipmentType !== "gateway";

  const shouldShowTPP = useMemo(() => {
    const selectedTPPEquipments = allSelectedEquipments
      .filter((e) => e.type === "apply_tpp")
      .map((e) => e.id);

    return (
      (!userOrgIsPaychex ||
        selectedTPPEquipments?.length === 0 ||
        equipment.type === "apply_tpp") &&
      !!equipment.apply_tpp
    );
  }, [allSelectedEquipments, equipment, userOrgIsPaychex]);

  const options = useMemo(() => {
    const enabledOptions = getEnabledPurchaseOptions(equipment);

    if (shouldShowTPP) {
      return enabledOptions;
    }

    return enabledOptions.filter((option) => option !== "apply_tpp");
  }, [equipment, shouldShowTPP]);

  const [purchaseType, setPurchaseType] = useState(() => {
    return options.length > 0 ? options[0] : null;
  });
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const defaults = {};
    Object.keys(PURCHASE_TYPE_MAPPINGS)
      .map((e) => `${e}_default`)
      .forEach((field) => {
        if (field in equipment) {
          defaults[field] = equipment[field] || "";
        }
      });
    defaultValues.current = defaults;
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (errorFields.has(name)) {
      const newErrorFields = new Set(errorFields);
      newErrorFields.delete(name);
      setErrorFields(newErrorFields);
    }

    onEquipmentChange({
      ...equipment,
      [name]: value,
    });
  };

  const handlePurchaseTypeChange = (val) => {
    const newPurchaseType = getPurchaseType(val);
    const oldDefaultFieldName = `${purchaseType}_default`;

    const updatedEquipment = {
      ...equipment,
      [oldDefaultFieldName]: defaultValues.current[oldDefaultFieldName] || "",
    };

    setPurchaseType(newPurchaseType);
    onEquipmentChange(updatedEquipment);
  };

  useEffect(() => {
    if (options.length > 0 && !purchaseType) {
      setPurchaseType(options[0]);
    }
  }, [options, purchaseType]);

  const handleSubmit = () => {
    const { hasErrors, errorFields: newErrorFields } =
      validateEquipment(equipment);

    setErrorFields(newErrorFields);

    if (!hasErrors) {
      handleAddConfiguredEquipment(purchaseType ?? "monthly_fee", quantity);
    }
  };

  return (
    <div className={styles.configurationContainer}>
      {!!equipment?.add_on_equipment && (
        <MultiSelect
          placeholder="Select Add-ons"
          id="add_on_equipment_default"
          name="add_on_equipment_default"
          value={
            equipment?.add_on_equipment_default?.split(",").map((id) => ({
              value: id,
              label:
                allEquipments.find((e) => e.id.toString() === id)
                  ?.equipment_name || id,
            })) || []
          }
          options={allEquipments
            .filter((e) =>
              equipment?.add_on_equipment_options.includes(e.id.toString()),
            )
            .map((e) => ({
              value: e.id.toString(),
              label: e.equipment_name,
            }))}
          onChange={(selectedOptions) => {
            handleChange({
              target: {
                name: "add_on_equipment_default",
                value: selectedOptions.map((opt) => opt.value).join(","),
              },
            });
          }}
          dropdownHeader="Select add-ons"
          showSelectAll
          selectedTextPrefix="Add-ons: "
        />
      )}

      {!!equipment?.sass_fee && (
        <TextInput
          label={"SaaS Fee"}
          placeholder={"0.00"}
          type="number"
          prefix={"$"}
          suffix={"/mo"}
          id="sass_fee_default"
          name="sass_fee_default"
          value={equipment?.sass_fee_default || ""}
          onChange={handleChange}
          error={errorFields.has("sass_fee_default")}
        />
      )}

      {!!equipment?.per_transaction && (
        <TextInput
          label={"Per Transaction Fee"}
          placeholder={"0.00"}
          type="number"
          prefix={"$"}
          suffix={"/txn"}
          id="per_transaction_default"
          name="per_transaction_default"
          value={equipment?.per_transaction_default || ""}
          onChange={handleChange}
          error={errorFields.has("per_transaction_default")}
        />
      )}

      {!!equipment?.amount && (
        <TextInput
          label={"Amount"}
          placeholder={"0.00"}
          type="number"
          prefix={"$"}
          id="amount_default"
          name="amount_default"
          value={equipment?.amount_default || ""}
          onChange={handleChange}
          error={errorFields.has("amount_default")}
        />
      )}

      {!!equipment?.factor_rate && (
        <TextInput
          label={"Factor Rate"}
          placeholder={"0.00"}
          type="number"
          suffix={"%"}
          id="factor_rate_default"
          name="factor_rate_default"
          value={equipment?.factor_rate_default || ""}
          onChange={handleChange}
          error={errorFields.has("factor_rate_default")}
        />
      )}

      {!!equipment?.payback && (
        <TextInput
          label={"Payback"}
          placeholder={"0"}
          type="number"
          suffix={" months"}
          id="payback_default"
          name="payback_default"
          value={equipment?.payback_default || ""}
          onChange={handleChange}
          error={errorFields.has("payback_default")}
        />
      )}

      {!!equipment?.monthly_wireless && (
        <TextInput
          label={"Monthly Wireless"}
          placeholder={"0.00"}
          type="number"
          prefix={"$"}
          suffix={"/mo"}
          id="monthly_wireless_default"
          name="monthly_wireless_default"
          value={equipment?.monthly_wireless_default || ""}
          onChange={handleChange}
          error={errorFields.has("monthly_wireless_default")}
        />
      )}

      {!!equipment?.text_sms_fee && (
        <TextInput
          label={"Text/SMS Fee"}
          placeholder={"0.00"}
          type="number"
          prefix={"$"}
          id="text_sms_fee_default"
          name="text_sms_fee_default"
          value={equipment?.text_sms_fee_default || ""}
          onChange={handleChange}
          error={errorFields.has("text_sms_fee_default")}
        />
      )}

      {!!equipment?.sim_card && (
        <TextInput
          label={"SIM Card Monthly"}
          placeholder={"0.00"}
          type="number"
          prefix={"$"}
          suffix={"/mo"}
          id="sim_card_monthly_default"
          name="sim_card_monthly_default"
          value={equipment?.sim_card_monthly_default || ""}
          onChange={handleChange}
          error={errorFields.has("sim_card_monthly_default")}
        />
      )}

      {/* Custom fields that need to be handled separately */}
      {!!equipment?.file_types && (
        <Select
          placeholder="File types"
          dropdownHeader="File types"
          options={equipment?.file_types_default?.split(",").map((type) => ({
            value: type,
            label: FILE_TYPE_OPTIONS[type],
          }))}
          value={
            equipment?.file_type_key
              ? {
                  value: equipment?.file_type_key,
                  label: FILE_TYPE_OPTIONS[equipment?.file_type_key],
                }
              : null
          }
          onChange={(selectedOption) =>
            handleChange({
              target: {
                name: "file_type_key",
                value: selectedOption,
              },
            })
          }
        />
      )}

      {!!equipment?.hardware && (
        <Select
          placeholder="Hardware"
          dropdownHeader="Hardware"
          options={allEquipments
            .filter((e) =>
              equipment.hardware_default.split(",").includes(e.id.toString()),
            )
            .map((hardware) => ({
              value: hardware.id,
              label: hardware.equipment_name,
            }))}
          value={equipment?.hardware_id}
          onChange={(selectedOption) =>
            handleChange({
              target: {
                name: "hardware_id",
                value: selectedOption,
              },
            })
          }
        />
      )}

      {!!equipment?.package && (
        <Select
          placeholder="Package"
          dropdownHeader="Package"
          options={Object.entries(PACKAGE_OPTIONS)
            .filter(([key]) =>
              equipment.package_default.split(",").includes(key),
            )
            .map(([key, value]) => ({
              value: key,
              label: value,
            }))}
          value={equipment?.package_key}
          onChange={(selectedOption) => {
            handleChange({
              target: {
                name: "package_key",
                value: selectedOption,
              },
            });

            if (selectedOption && PACKAGE_PRICING_MAPPING[selectedOption]) {
              handleChange({
                target: {
                  name: "monthly_fee_default",
                  value: PACKAGE_PRICING_MAPPING[selectedOption],
                },
              });
            }
          }}
        />
      )}

      {!!equipment?.other_equipments && (
        <Select
          placeholder="Other Equipment"
          dropdownHeader="Other Equipment"
          options={allEquipments
            .filter((e) =>
              equipment.other_equipments_default
                .split(",")
                .includes(e.id.toString()),
            )
            .map((other) => ({
              value: other.id,
              label: other.equipment_name,
            }))}
          value={equipment?.other_equipment_id}
          onChange={(selectedOption) =>
            handleChange({
              target: {
                name: "other_equipment_id",
                value: selectedOption,
              },
            })
          }
        />
      )}

      {!!equipment?.pin_debit && (
        <div className={styles.checkboxContainer}>
          <Checkbox
            label="Add PIN Debit"
            id="pin_debit_default"
            name="pin_debit_default"
            checked={equipment?.pin_debit_default === "yes" ? true : false}
            onChange={(e) => {
              handleChange({
                target: {
                  name: "pin_debit_default",
                  value: e.target.checked ? "yes" : "no",
                },
              });
            }}
          />
          <label htmlFor="pin_debit_default">Add PIN Debit</label>
        </div>
      )}

      {!!equipment?.cable_length && (
        <Select
          placeholder="Cable Length"
          dropdownHeader="Cable Length"
          options={Object.entries(CABLE_LENGTH_OPTIONS).map(([key, value]) => ({
            value: key,
            label: value,
          }))}
          value={
            equipment?.cable_length_key
              ? {
                  value: equipment?.cable_length_key,
                  label: CABLE_LENGTH_OPTIONS[equipment?.cable_length_key],
                }
              : null
          }
          onChange={(selectedOption) =>
            handleChange({
              target: {
                name: "cable_length_key",
                value: selectedOption,
              },
            })
          }
        />
      )}

      {!!equipment?.connectivity && (
        <Select
          placeholder="Connectivity"
          dropdownHeader="Connectivity"
          options={equipment?.connectivity_options?.split(",").map((type) => ({
            value: type,
            label: CONNECTIVITY_OPTIONS[type],
          }))}
          value={
            equipment?.connectivity_default
              ? {
                  value: equipment?.connectivity_default,
                  label: CONNECTIVITY_OPTIONS[equipment?.connectivity_default],
                }
              : null
          }
          onChange={(selectedOption) =>
            handleChange({
              target: {
                name: "connectivity_default",
                value: selectedOption,
              },
            })
          }
        />
      )}

      {equipment?.connectivity_default === "sim" && (
        <>
          <Select
            placeholder="SIM Provided By"
            dropdownHeader="SIM Provided By"
            options={[
              {
                value: "processor_provided",
                label: "Processor Provided",
              },
              {
                value: "merchant_provided",
                label: "Merchant Provided",
              },
            ]}
            value={equipment?.sim_provided_by}
            onChange={(selectedOption) =>
              handleChange({
                target: { name: "sim_provided_by", value: selectedOption },
              })
            }
          />

          {equipment?.sim_provided_by === "processor_provided" && (
            <>
              {!!equipment?.monthly_cellular_txn && (
                <TextInput
                  label={"Monthly Cellular Transactions"}
                  placeholder={"0"}
                  type="number"
                  id="monthly_cellular_txn_default"
                  name="monthly_cellular_txn_default"
                  value={equipment?.monthly_cellular_txn_default || ""}
                  onChange={handleChange}
                  error={errorFields.has("monthly_cellular_txn_default")}
                />
              )}

              {!!equipment?.cellular_per_txn && (
                <TextInput
                  label={"Cellular Per Transaction"}
                  placeholder={"0.00"}
                  type="number"
                  prefix={"$"}
                  suffix={"/txn"}
                  id="cellular_per_txn_default"
                  name="cellular_per_txn_default"
                  value={equipment?.cellular_per_txn_default || ""}
                  onChange={handleChange}
                  error={errorFields.has("cellular_per_txn_default")}
                />
              )}
            </>
          )}
        </>
      )}

      {!!equipment?.pin_pad && (
        <TextInput
          label="PIN Pad Name"
          placeholder="PIN Pad Name"
          type="text"
          id="pin_pad_name_default"
          name="pin_pad_name_default"
          value={equipment?.pin_pad_name_default || ""}
          onChange={handleChange}
          error={errorFields.has("pin_pad_name_default")}
        />
      )}

      {!!equipment?.customer_facing && (
        <div className={styles.checkboxContainer}>
          <Checkbox
            label="Customer Facing"
            id="customer_facing_default"
            name="customer_facing_default"
            checked={
              equipment?.customer_facing_default === "yes" ? true : false
            }
            onChange={(e) => {
              handleChange({
                target: {
                  name: "customer_facing_default",
                  value: e.target.checked ? "yes" : "no",
                },
              });
            }}
          />
          <label htmlFor="customer_facing_default">Customer Facing</label>
        </div>
      )}

      {!!equipment?.virtual_terminal && (
        <div className={styles.checkboxContainer}>
          <Checkbox
            label="Virtual Terminal"
            id="virtual_terminal_default"
            name="virtual_terminal_default"
            checked={
              equipment?.virtual_terminal_default === "yes" ? true : false
            }
            onChange={(e) => {
              handleChange({
                target: {
                  name: "virtual_terminal_default",
                  value: e.target.checked ? "yes" : "no",
                },
              });
            }}
          />
          <label htmlFor="virtual_terminal_default">Virtual Terminal</label>
        </div>
      )}

      {!!equipment?.monthly_fee && (
        <TextInput
          label={"Monthly Fee"}
          placeholder={"0.00"}
          type="number"
          prefix={"$"}
          suffix={"/mo"}
          id="monthly_fee_default"
          name="monthly_fee_default"
          value={equipment?.monthly_fee_default || ""}
          onChange={handleChange}
          error={errorFields.has("monthly_fee_default")}
        />
      )}

      {options?.length >= 1 && (
        <>
          {options.length > 1 && (
            <SlidingTab
              options={options.map((option) => getButtonText(option))}
              selected={getButtonText(purchaseType)}
              onChange={handlePurchaseTypeChange}
            />
          )}

          {equipment &&
            purchaseType &&
            !!equipment?.[purchaseType] &&
            purchaseType !== "apply_tpp" && (
              <TextInput
                label={
                  options.length === 1
                    ? purchaseType
                        ?.replace(/_/g, " ")
                        ?.replace(/\b\w/g, (l) => l.toUpperCase())
                    : ""
                }
                placeholder={"0.00"}
                type="number"
                prefix={"$"}
                suffix={
                  purchaseType === "rent_for" ||
                  purchaseType === "lease_for" ||
                  purchaseType === "monthly_fee"
                    ? "/mo"
                    : undefined
                }
                id={`${purchaseType}_default`}
                name={`${purchaseType}_default`}
                value={equipment?.[`${purchaseType}_default`] || ""}
                readOnly={purchaseType === "purchase_for" && userOrgIsPaychex}
                onChange={handleChange}
                error={errorFields.has(`${purchaseType}_default`)}
              />
            )}
        </>
      )}

      {isNotAGateway && (
        <StepperInput
          name="quantity"
          min={1}
          value={1}
          id={`quantity-eqp`}
          onChange={(value) => {
            setQuantity(value);
          }}
        />
      )}

      <div className={styles.buttonContainer}>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={errorFields.size > 0}
          fullWidth
        >
          {isNotAGateway ? getButtonText(purchaseType) : "Select"}
        </Button>
      </div>
    </div>
  );
};

export default EquipmentConfigurator;
